import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { MotionPathPlugin } from 'gsap/MotionPathPlugin';

gsap.registerPlugin(ScrollTrigger, MotionPathPlugin);

export default function initStorytellingInflationGraph() {
    const container = document.querySelector('.graph-container');
    const svg = document.querySelector('.inflation-svg');

    if (!container || !svg) return;

    const horizontalLines = svg.querySelectorAll('#horizontalLinesGroup line');
    const axisText = svg.querySelectorAll('#graphTextGroup text');
    const sonoFixedLine = svg.querySelector('#sonoFixedLine');
    const bottomSonoLabel = svg.querySelector('#bottomSonoLabel');

    const graphLine = svg.querySelector('#graphLine'); // Red Path
    const movingDot = svg.querySelector('#movingDot'); // The slider handle

    const hoverPoints = svg.querySelectorAll('.hover-point');
    const pointCircles = svg.querySelectorAll('.hover-point circle'); // 포인트 내부 원들
    const bubbles = svg.querySelectorAll('.hover-bubble');
    const savingTexts = svg.querySelectorAll('#savingTextGroup text');
    const topFutureLabel = svg.querySelector('#topFutureLabel');

    // 현재 핸들의 위치(이미지 상의 progress)를 추적할 객체
    let tracker = { progress: 0 };

    const isMobile = window.innerWidth <= 768;

    // 1. 초기 상태 설정
    gsap.set(horizontalLines, { scaleX: 0, transformOrigin: "left center" });
    gsap.set(axisText, { autoAlpha: 0, y: "+=10" });
    gsap.set(sonoFixedLine, { scaleX: 0, transformOrigin: "left center" });

    // 데스크탑 정위치(700, 310)와 모바일 전용 위치 분리 초기화
    gsap.set(bottomSonoLabel, {
        autoAlpha: 0,
        scale: isMobile ? 1 : 0.8,
        x: isMobile ? 450 : 700,
        y: isMobile ? 320 : 310
    });

    // 683만 라벨 또한 초기 위치 명시 (데스크탑: 590, 0 / 모바일: 400, 10)
    gsap.set(topFutureLabel, {
        autoAlpha: 0,
        x: isMobile ? 400 : 590,
        y: isMobile ? 10 : 0,
        scale: isMobile ? 0.8 : 0.8
    });

    // 레드 곡선 초기화
    if (graphLine) {
        const length = graphLine.getTotalLength();
        gsap.set(graphLine, {
            strokeDasharray: length,
            strokeDashoffset: length,
            autoAlpha: 1
        });
    }

    // 포인트 및 텍스트 초기화
    gsap.set(hoverPoints, { scale: 0, autoAlpha: 0, transformOrigin: "center center" });
    gsap.set(pointCircles, { autoAlpha: 1 }); // 포인트 원 기본 노출
    gsap.set(bubbles, { scale: 0, autoAlpha: 0, transformOrigin: "center bottom" });
    gsap.set(savingTexts, { scale: 0, autoAlpha: 0, transformOrigin: "center center" });
    gsap.set(topFutureLabel, { autoAlpha: 0, y: -20, scale: 0.8 });
    gsap.set(movingDot, { autoAlpha: 0, scale: 0, x: 0, y: 0, transformOrigin: "50% 50%" });

    // 2. 애니메이션 타임라인
    const tl = gsap.timeline({
        scrollTrigger: {
            trigger: container,
            start: "top 80%",
            once: true
        }
    });

    // STEP 1: 무대 세팅
    if (!isMobile) {
        tl.to(horizontalLines, {
            scaleX: 1, duration: 0.8, stagger: 0.1, ease: "power2.out"
        });
    }

    tl.to(axisText, {
        autoAlpha: 1, y: 0, duration: 0.5, stagger: 0.05, ease: "back.out(1.7)"
    }, isMobile ? "0" : "-=0.4")
        .to(sonoFixedLine, {
            scaleX: 1, duration: 1, ease: "power3.out"
        }, "-=0.2")
        .to(bottomSonoLabel, {
            autoAlpha: 1,
            scale: isMobile ? 1.2 : 1,
            x: isMobile ? 450 : 700, // 데스크탑 정위치(700) 복구
            y: isMobile ? 320 : 310, // 데스크탑 정위치(310) 복구
            duration: 0.6,
            ease: isMobile ? "back.out(2)" : "elastic.out(1, 0.5)"
        }, "-=0.4")

    // STEP 2: 물가 상승 (시각화의 핵심)
    if (graphLine && movingDot) {
        tl.set(movingDot, {
            autoAlpha: 1, scale: 1,
            motionPath: { path: graphLine, align: graphLine, alignOrigin: [0.5, 0.5], start: 0, end: 0 }
        }, "+=0.2")

            // 곡선 드로잉 + 점 이동 (3.5초 동안 드라마틱하게)
            .to(graphLine, { strokeDashoffset: 0, duration: 3.5, ease: "power2.inOut" })
            .to(tracker, {
                progress: 1,
                duration: 3.5,
                ease: "power2.inOut",
                onUpdate: function () {
                    gsap.set(movingDot, {
                        motionPath: {
                            path: graphLine,
                            align: graphLine,
                            alignOrigin: [0.5, 0.5],
                            start: tracker.progress,
                            end: tracker.progress
                        }
                    });
                }
            }, "<");

        // 이동하는 동안 각 연도별 포인트와 텍스트 등장 동기화
        tl.to(hoverPoints[0], { autoAlpha: 1, scale: 1, duration: 0.3 }, "-=3.4")
            .to(savingTexts[0], { autoAlpha: 1, scale: 1, duration: 0.4, ease: "back.out(1.7)" }, "<")

        // 5년 데이터 (모바일에서는 스킵)
        if (!isMobile) {
            tl.to(hoverPoints[1], { autoAlpha: 1, scale: 1, duration: 0.3 }, "-=2.6")
                .to(bubbles[1], { autoAlpha: 1, scale: 1, duration: 0.4, ease: "back.out(2)" }, "<")
                .to(savingTexts[1], { autoAlpha: 1, scale: 1, duration: 0.4, ease: "back.out(1.7)" }, "<")
        }

        // 10년 데이터
        tl.to(hoverPoints[2], { autoAlpha: 1, scale: 1, duration: 0.3 }, isMobile ? "-=1.8" : "-=1.8")
            .to(bubbles[2], { autoAlpha: 1, scale: 1, duration: 0.4, ease: "back.out(2)" }, "<")
            .to(savingTexts[2], { autoAlpha: 1, scale: 1, duration: 0.4, ease: "back.out(1.7)" }, "<")

        // 15년 데이터 (모바일에서는 스킵)
        if (!isMobile) {
            tl.to(hoverPoints[3], { autoAlpha: 1, scale: 1, duration: 0.3 }, "-=1.0")
                .to(bubbles[3], { autoAlpha: 1, scale: 1, duration: 0.4, ease: "back.out(2)" }, "<")
                .to(savingTexts[3], { autoAlpha: 1, scale: 1, duration: 0.4, ease: "back.out(1.7)" }, "<")
        }

        // 20년 데이터 (최종)
        tl.to(hoverPoints[4], { autoAlpha: 1, scale: 1, duration: 0.3 }, "-=0.2")
            // 20년 지점의 하얀 말풍선(bubbles[4]) 노출 제거 (레드 라벨로 대체)

            .to(topFutureLabel, {
                autoAlpha: 1,
                // 데스크탑 전용 좌표(590, 0)로 하향 조정하여 하얀 말풍선 자리 대체
                ...(isMobile ? { x: 400, y: 10, scale: 1.3 } : { x: 590, y: 0, scale: 1 }),
                duration: 0.8,
                ease: isMobile ? "back.out(2)" : "elastic.out(1, 0.5)"
            }, "-=0.2")
            .to(savingTexts[4], { autoAlpha: 1, scale: 1.2, duration: 0.6, ease: "back.out(2)" }, "<");
    }

    // 3. 인터랙션: 포인트 클릭 시 슬라이더 이동 및 텍스트 강조
    hoverPoints.forEach((point, i) => {
        point.style.cursor = 'pointer';
        point.addEventListener('click', () => {
            const targetPos = i * 0.25;

            // 이동 시작 시 모든 포인트 원을 다시 노출
            gsap.to(pointCircles, { autoAlpha: 1, duration: 0.2 });

            // 현재 트래커의 progress에서 targetPos까지 '미끄러지듯' 애니메이션
            gsap.to(tracker, {
                progress: targetPos,
                duration: 1.2,
                ease: "power3.inOut",
                onUpdate: () => {
                    gsap.set(movingDot, {
                        motionPath: {
                            path: graphLine,
                            align: graphLine,
                            alignOrigin: [0.5, 0.5],
                            start: tracker.progress,
                            end: tracker.progress
                        }
                    });
                },
                onComplete: () => {
                    // 도착 후 해당 포인트의 원만 숨겨서 겹침 현상(이중 원) 제거
                    gsap.to(pointCircles[i], { autoAlpha: 0, duration: 0.1 });

                    // 데이터 강조
                    savingTexts.forEach((txt, idx) => {
                        if (i === idx) {
                            gsap.to(txt, { scale: 1.8, fill: "#FFF", duration: 0.5, ease: "back.out(2)" });
                        } else {
                            gsap.to(txt, { scale: 1, fill: "#C5A065", duration: 0.3 });
                        }
                    });

                    bubbles.forEach((b, idx) => {
                        // 20년 지점(idx === 4)은 레드 라벨이 대체하므로 하얀 말풍선 숨김 유지
                        if (i === idx && idx !== 4) {
                            gsap.to(b, { autoAlpha: 1, scale: 1.2, duration: 0.4, ease: "back.out(1.7)" });
                        } else {
                            gsap.to(b, { autoAlpha: 0, scale: 0, duration: 0.2 });
                        }
                    });

                    if (i === 4) {
                        // 새 위치(y: 0) 기준으로 강조 효과 조정 (y: -10)
                        gsap.to(topFutureLabel, {
                            autoAlpha: 1,
                            scale: 1.15,
                            duration: 0.5,
                            y: isMobile ? 0 : -10,
                            ease: "back.out(2)"
                        });
                    } else {
                        // 기본 위치(y: 0)로 복귀
                        gsap.to(topFutureLabel, { scale: 1, y: isMobile ? 10 : 0, duration: 0.3 });
                    }
                }
            });
        });
    });
}
